import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Search } from "lucide-react";

// Normaliza opciones de variante: pueden llegar como string o como {nombre, precio_extra}
function optLabel(o) {
  if (o == null) return "";
  if (typeof o === "string") return o;
  if (typeof o === "object") return String(o.nombre || o.label || o.value || "");
  return String(o);
}
function optList(arr) { return (arr || []).map(optLabel).filter(Boolean); }

// Calcula el precio_extra sumado de las variantes seleccionadas
function variantExtra(prod, sel) {
  if (!prod || !sel) return 0;
  let total = 0;
  const axes = [
    ["opciones_color", sel.color],
    ["opciones_estilo", sel.estilo],
    ["opciones_tela", sel.tela],
    ["opciones_tamano", sel.tamano],
  ];
  for (const [k, val] of axes) {
    if (!val) continue;
    for (const o of (prod[k] || [])) {
      if (o && typeof o === "object" && String(o.nombre || "") === String(val)) {
        total += Number(o.precio_extra || 0);
        break;
      }
    }
  }
  return total;
}

// items iniciales del pedido: los normalizamos al formato del payload
function toPayloadItem(it) {
  return {
    product_id: String(it.product_id),
    variant_id: it.variant_id || null,
    color: it.color || "",
    estilo: it.estilo || "",
    tela: it.tela || "",
    tamano: it.tamano || "",
    cantidad: Math.max(1, Math.floor(Number(it.cantidad) || 1)),
    precio_unit: Number(it.precio_unit) || 0,
    descuento: Number(it.descuento) || 0,
    modificadores: [],  // los modificadores originales se preservan solo si vienen como {id, nombre, tipo, precio_extra}
  };
}

export default function EditarProductosDialog({ open, onOpenChange, order, onSaved }) {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && order) {
      // Cargamos productos primero para poder llenar las opciones de variantes existentes
      api.get("/products").then(({ data }) => {
        const prods = data || [];
        setProducts(prods);
        const prodMap = Object.fromEntries(prods.map((p) => [p.id, p]));
        const initialItems = (order.items || []).map((it) => {
          const prod = prodMap[it.product_id] || {};
          return {
            ...toPayloadItem(it),
            product_name: it.product_name || prod.nombre || "",
            _opciones: {
              color: optList(prod.opciones_color),
              tamano: optList(prod.opciones_tamano),
              estilo: optList(prod.opciones_estilo),
              tela: optList(prod.opciones_tela),
            },
          };
        });
        setItems(initialItems);
      }).catch(() => {
        // Sin catálogo, dejamos los items sin opciones editables
        setItems((order.items || []).map((it) => ({ ...toPayloadItem(it), product_name: it.product_name || "" })));
      });
      setQ("");
      setAddOpen(false);
    }
  }, [open, order]);

  const totalCalculado = useMemo(() => {
    const sub = items.reduce((acc, it) => {
      const prod = products.find((p) => p.id === it.product_id);
      const ve = variantExtra(prod, it);
      const p = (Number(it.precio_unit || 0) * (1 - Number(it.descuento || 0) / 100)) + ve;
      return acc + p * Number(it.cantidad || 0);
    }, 0);
    return sub + Number(order?.envio_precio || 0);
  }, [items, order, products]);

  const filteredProds = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products.slice(0, 50);
    return products.filter(
      (p) =>
        (p.nombre || "").toLowerCase().includes(term) ||
        (p.sku || "").toLowerCase().includes(term)
    ).slice(0, 50);
  }, [products, q]);

  const addProduct = (prod) => {
    const nuevo = {
      product_id: prod.id,
      product_name: prod.nombre,
      variant_id: null,
      color: optList(prod.opciones_color)[0] || "",
      tamano: optList(prod.opciones_tamano)[0] || "",
      estilo: optList(prod.opciones_estilo)[0] || "",
      tela: optList(prod.opciones_tela)[0] || "",
      cantidad: 1,
      precio_unit: Number(prod.precio || 0),
      descuento: 0,
      modificadores: [],
      // guardamos el catálogo de opciones para el editor
      _opciones: {
        color: optList(prod.opciones_color),
        tamano: optList(prod.opciones_tamano),
        estilo: optList(prod.opciones_estilo),
        tela: optList(prod.opciones_tela),
      },
    };
    setItems((prev) => [...prev, nuevo]);
    setAddOpen(false);
    setQ("");
  };

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const guardar = async () => {
    if (items.length === 0) {
      toast.error("El pedido debe tener al menos 1 producto");
      return;
    }
    // Validar cantidades y precios positivos
    for (const [i, it] of items.entries()) {
      if (Number(it.cantidad || 0) <= 0) {
        toast.error(`Item ${i + 1}: cantidad debe ser mayor a 0`);
        return;
      }
      if (Number(it.precio_unit || 0) < 0) {
        toast.error(`Item ${i + 1}: precio no puede ser negativo`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { items: items.map(toPayloadItem) };
      const { data } = await api.patch(`/orders/${order.id}/items`, payload);
      toast.success("Productos actualizados");
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="editar-productos-dialog">
        <DialogHeader>
          <DialogTitle>Editar productos del pedido {order?.numero}</DialogTitle>
          <DialogDescription>
            Puedes agregar, quitar o modificar productos. El total y stock se recalcularán automáticamente.
            {order?.facturas?.length > 0 && (
              <span className="block mt-2 text-amber-600 font-semibold">
                ⚠️ Este pedido ya tiene facturas emitidas. El nuevo total puede requerir ajuste manual del estado de facturación.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-6 text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md">
              No hay productos. Agrega al menos uno.
            </div>
          ) : (
            items.map((it, idx) => {
              const opts = it._opciones || { color: [], tamano: [], estilo: [], tela: [] };
              return (
              <div key={idx} className="border border-zinc-200 rounded-md p-3 space-y-2" data-testid={`edit-item-row-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">
                      {it.product_name || products.find((p) => p.id === it.product_id)?.nombre || it.product_id}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {[it.color, it.tamano, it.estilo, it.tela].filter(Boolean).join(" · ") || "Sin variante"}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeItem(idx)} data-testid={`remove-item-btn-${idx}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
                {(opts.color.length > 0 || opts.tamano.length > 0 || opts.estilo.length > 0 || opts.tela.length > 0) && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {opts.color.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase text-zinc-500">Color</Label>
                        <select
                          className="w-full h-9 rounded-md border border-zinc-300 bg-white text-sm px-2"
                          value={it.color || ""}
                          onChange={(e) => updateItem(idx, { color: e.target.value })}
                          data-testid={`edit-item-color-${idx}`}
                        >
                          <option value="">—</option>
                          {opts.color.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    )}
                    {opts.tamano.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase text-zinc-500">Tamaño</Label>
                        <select
                          className="w-full h-9 rounded-md border border-zinc-300 bg-white text-sm px-2"
                          value={it.tamano || ""}
                          onChange={(e) => updateItem(idx, { tamano: e.target.value })}
                          data-testid={`edit-item-tamano-${idx}`}
                        >
                          <option value="">—</option>
                          {opts.tamano.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    )}
                    {opts.estilo.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase text-zinc-500">Estilo</Label>
                        <select
                          className="w-full h-9 rounded-md border border-zinc-300 bg-white text-sm px-2"
                          value={it.estilo || ""}
                          onChange={(e) => updateItem(idx, { estilo: e.target.value })}
                          data-testid={`edit-item-estilo-${idx}`}
                        >
                          <option value="">—</option>
                          {opts.estilo.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    )}
                    {opts.tela.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase text-zinc-500">Tela</Label>
                        <select
                          className="w-full h-9 rounded-md border border-zinc-300 bg-white text-sm px-2"
                          value={it.tela || ""}
                          onChange={(e) => updateItem(idx, { tela: e.target.value })}
                          data-testid={`edit-item-tela-${idx}`}
                        >
                          <option value="">—</option>
                          {opts.tela.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase text-zinc-500">Cantidad</Label>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      value={it.cantidad}
                      onChange={(e) => updateItem(idx, { cantidad: Math.max(1, Math.floor(Number(e.target.value) || 1)) })}
                      data-testid={`edit-item-cantidad-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-zinc-500">Precio unit.</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={it.precio_unit}
                      onChange={(e) => updateItem(idx, { precio_unit: Number(e.target.value) || 0 })}
                      data-testid={`edit-item-precio-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-zinc-500">Descuento %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={it.descuento}
                      onChange={(e) => updateItem(idx, { descuento: Number(e.target.value) || 0 })}
                      data-testid={`edit-item-descuento-${idx}`}
                    />
                  </div>
                </div>
                <div className="text-right text-xs text-zinc-500">
                  {(() => {
                    const prod = products.find((p) => p.id === it.product_id);
                    const ve = variantExtra(prod, it);
                    const linea = (Number(it.precio_unit || 0) * (1 - Number(it.descuento || 0) / 100) + ve) * Number(it.cantidad || 0);
                    return (
                      <>
                        {ve > 0 && (
                          <span className="mr-2 text-emerald-700">+{formatMoney(ve)} variante</span>
                        )}
                        Subtotal item: <span className="font-semibold text-zinc-900">{formatMoney(linea)}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
              );
            })
          )}

          {addOpen ? (
            <div className="border-2 border-dashed border-[#002FA7] rounded-md p-3 space-y-2 bg-blue-50/40">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-zinc-500" />
                <Input
                  autoFocus
                  placeholder="Buscar producto por nombre o SKU..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="add-product-search"
                />
                <Button variant="ghost" size="sm" onClick={() => { setAddOpen(false); setQ(""); }}>
                  Cancelar
                </Button>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-zinc-200 border border-zinc-200 rounded bg-white">
                {filteredProds.length === 0 ? (
                  <div className="p-3 text-xs text-zinc-500">Sin resultados</div>
                ) : (
                  filteredProds.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => addProduct(p)}
                      className="w-full text-left p-2 text-sm hover:bg-zinc-50 flex justify-between items-center"
                      data-testid={`add-product-option-${p.id}`}
                    >
                      <span className="truncate">{p.nombre}</span>
                      <span className="text-xs text-zinc-500 tabular-nums">{formatMoney(p.precio)}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setAddOpen(true)}
              className="w-full"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Agregar producto
            </Button>
          )}
        </div>

        <div className="mt-2 flex justify-between items-center border-t border-zinc-200 pt-3">
          <div className="text-sm text-zinc-500">
            {items.length} producto{items.length !== 1 ? "s" : ""} · Envío {formatMoney(order?.envio_precio || 0)}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Nuevo total</div>
            <div className="text-2xl font-black tabular-nums">{formatMoney(totalCalculado)}</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="editar-productos-cancelar">
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={saving} data-testid="editar-productos-guardar">
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
